import React, { useCallback } from 'react';
import { List, X } from 'lucide-react';

const PDFTableOfContents = ({
  showTableOfContents, setShowTableOfContents,
  tableOfContents, tocLoading,
  pageNumber, goToPage, docInfo,
  continuousMode, containerRef,
  toolbarHeight = 56,
}) => {
  if (!showTableOfContents) return null;

  const handleTocClick = useCallback((pageNum) => {
    if (continuousMode && containerRef?.current) {
      // En modo continuo: scroll suave a la pagina
      const container = containerRef.current;
      const targetEl = container.querySelector(`[data-page-num="${pageNum}"]`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // No cerramos el TOC en modo continuo para poder seguir navegando
        return;
      }
    }
    // Modo pagina individual: navegar y cerrar
    goToPage(pageNum);
    setShowTableOfContents(false);
  }, [continuousMode, containerRef, goToPage, setShowTableOfContents]);

  return (
    <div
      className={`${continuousMode ? 'fixed left-0 z-[60]' : 'absolute top-0 left-0 h-full z-50'} w-80 bg-slate-800/95 backdrop-blur-sm border-r border-slate-700 shadow-2xl overflow-hidden flex flex-col`}
      style={continuousMode ? { top: `${toolbarHeight}px`, height: `calc(100vh - ${toolbarHeight}px)` } : undefined}
    >
      <div className="p-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <List className="h-5 w-5 text-teal-400" />
            {tableOfContents.length > 0 
              ? (tableOfContents[0]?.isGenerated ? 'Navegacion Rapida' : 'Indice del Documento')
              : 'Indice'}
          </h3>
          <button 
            onClick={() => setShowTableOfContents(false)}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-slate-400">
          {tocLoading 
            ? 'Cargando indice...'
            : tableOfContents.length > 0 
              ? (tableOfContents[0]?.isGenerated 
                ? `${docInfo?.total_pages || 0} paginas` 
                : `${tableOfContents.length} secciones`)
              : 'Sin indice disponible'}
          {continuousMode && !tocLoading && tableOfContents.length > 0 && (
            <span className="text-teal-400 ml-1">- Toca para navegar</span>
          )}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {tocLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-slate-400 text-sm">Cargando...</div>
          </div>
        ) : tableOfContents.length > 0 ? (
          <div className="space-y-1">
            {tableOfContents.map((item, index) => (
              <button
                key={`toc-${item.page_num}-${item.title?.slice(0,20)}`}
                onClick={() => handleTocClick(item.page_num)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-teal-500/20 ${
                  pageNumber === item.page_num ? 'bg-teal-500/30 text-teal-300' : 'text-slate-300'
                }`}
                style={{ paddingLeft: `${(item.level - 1) * 16 + 12}px` }}
              >
                <div className="flex items-start gap-2">
                  <span className="text-teal-500 text-xs mt-1 flex-shrink-0">
                    {item.page_num}
                  </span>
                  <span className={`text-sm ${item.level === 1 ? 'font-medium' : ''}`}>
                    {item.title}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-32">
            <div className="text-slate-400 text-sm text-center">
              Este documento no tiene indice embebido.<br/>
              <span className="text-xs">Es posible que sea un PDF escaneado.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFTableOfContents;
